/**
 * KND-3c — what the "Kündigungsbestätigung" panel shows, read off the server's
 * answer (`GET …/termination/confirmations`). Pure functions, no requests.
 *
 * NOTHING IS DECIDED HERE. Readiness, the current version and whether it is
 * stale are the server's verdicts (`readiness`, `current_state`,
 * `current_confirmation.is_stale`, each version's `lifecycle`). This module
 * only maps them to a view and to German labels. An answer it does not
 * recognise is `unknown` — never read as "nothing there yet", which would
 * offer a generation the server never allowed.
 */
import { formatDateOnly, formatDateTime } from "../../../../utils/datetime";

const TERMINATION_STATES = new Set(["none", "recorded", "legacy", "withdrawn"]);
const CURRENT_STATES = new Set(["none", "current", "stale"]);

/**
 * `hidden` — nothing to show (no termination act, no version);
 * `not_created` — B: recorded, ready, no version yet;
 * `not_ready` — C: recorded, not ready;
 * `current` — D; `stale` — E;
 * `history_only` — F/G: no active termination, earlier versions exist;
 * `unknown` — an answer this client cannot read.
 */
export function confirmationPanelView(data) {
  if (!data || typeof data !== "object") return "unknown";
  const terminationState = data.termination_state;
  const currentState = data.current_state;
  if (!TERMINATION_STATES.has(terminationState)) return "unknown";
  if (!CURRENT_STATES.has(currentState)) return "unknown";
  if (!Array.isArray(data.versions)) return "unknown";

  if (terminationState === "recorded") {
    if (currentState === "current" || currentState === "stale") {
      return data.current_confirmation ? currentState : "unknown";
    }
    return data.readiness?.ready === true ? "not_created" : "not_ready";
  }
  // Withdrawn, legacy or none: nothing may be generated; history stays.
  if (currentState !== "none") return "unknown";
  return data.versions.length ? "history_only" : "hidden";
}

/** May the panel offer a generation in this view? (B and E — and only when
 *  the server says the data is ready.) */
export function canOfferGeneration(view, data) {
  if (view === "not_created") return true;
  if (view === "stale") return data?.readiness?.ready === true;
  return false;
}

/** The server's own messages for what is missing, in its order. */
export function readinessMessages(data) {
  const issues = Array.isArray(data?.readiness?.issues) ? data.readiness.issues : [];
  return issues
    .map((issue) => (typeof issue?.message === "string" ? issue.message.trim() : ""))
    .filter(Boolean);
}

const LIFECYCLE_LABELS = {
  current: "aktuell",
  superseded: "ersetzt",
  previous_termination: "früherer Kündigungsvorgang",
  termination_withdrawn: "Kündigung zurückgenommen",
};

export function lifecycleLabel(lifecycle) {
  return LIFECYCLE_LABELS[lifecycle] || "Version";
}

/** Every version except the current one, newest first (the server's order). */
export function otherVersions(data) {
  const versions = Array.isArray(data?.versions) ? data.versions : [];
  const currentId = data?.current_confirmation?.id ?? null;
  return versions.filter((v) => v && v.id !== currentId);
}

/** "v2 · früherer Kündigungsvorgang · 12.09.2026" */
export function versionLine(version) {
  return [
    `v${version.version}`,
    lifecycleLabel(version.lifecycle),
    formatDateOnly(version.generated_at),
  ].join(" · ");
}

/** "Generiert am 01.10.2026, 10:15" (+ " · automatisch"). */
export function generatedAtText(confirmation) {
  const at = `Generiert am ${formatDateTime(confirmation?.generated_at)}`;
  return confirmation?.generation_trigger === "auto" ? `${at} · automatisch` : at;
}

/** Accessible name of one version's PDF button: the visible "PDF öffnen" plus
 *  the version, so several buttons in one panel are told apart. */
export function pdfButtonLabel(version) {
  return `PDF öffnen, Version ${version.version}`;
}
